import React, { FC } from 'react'
import classnames from 'classnames/bind'
import { Dropdown, DropdownProps } from 'semantic-ui-react'
import Icon from '../icon'
import Text from '../text'
import { ColorNames } from '../../../constants/colorVars'
import type { AutomationTarget } from '../../../types'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface EnhancedDropdownOption {
  value: string | number
  text: string
}

export interface EnhancedDropdownProps extends AutomationTarget {
  options: EnhancedDropdownOption[]
  selectedValue?: string | number
  onSelect?: (value: string | number) => void
  variant?: 'default' | 'enhanced'
  direction?: DropdownProps['direction']
  disabled?: boolean
  inline?: boolean
  text?: string
  icon?: DropdownProps['icon']
  className?: string
  trigger?: React.ReactNode
  [key: string]: unknown
}

const EnhancedDropdown: FC<EnhancedDropdownProps> = ({
  options,
  selectedValue,
  onSelect,
  variant = 'default',
  direction = 'left',
  disabled = false,
  inline = false,
  text,
  icon,
  className,
  trigger,
  elmId = 'enhanced-dropdown',
  ...rest
}) => {
  if (variant === 'enhanced') {
    return (
      <Dropdown
        data-elm-id={elmId}
        direction={direction}
        disabled={disabled}
        inline={inline}
        text={text}
        icon={icon}
        trigger={trigger}
        className={cx('enhanced-dropdown', className)}
        {...rest}
      >
        <Dropdown.Menu className={cx('enhanced-menu')}>
          {options.map(option => {
            const isSelected = String(selectedValue) === String(option.value)
            return (
              <React.Fragment key={option.value}>
                <div className={cx('enhanced-divider')} />
                <div
                  className={cx('enhanced-item', { selected: isSelected })}
                  data-elm-id={`${elmId}_${option.value}`}
                  onClick={() => onSelect?.(option.value)}
                >
                  <div className={cx('enhanced-item-content')}>
                    {isSelected && (
                      <Icon
                        name='check'
                        iconStyle='regular'
                        color={ColorNames.Info}
                        size='large'
                        elmId={`${elmId}_${option.value}_check`}
                      />
                    )}
                    <Text.Body
                      size='normal'
                      color={ColorNames.Gray}
                      weight='heavy'
                    >
                      {option.text}
                    </Text.Body>
                  </div>
                </div>
              </React.Fragment>
            )
          })}
        </Dropdown.Menu>
      </Dropdown>
    )
  }

  return (
    <Dropdown
      data-elm-id={elmId}
      direction={direction}
      disabled={disabled}
      inline={inline}
      text={text}
      icon={icon}
      trigger={trigger}
      className={className}
      {...rest}
    >
      <Dropdown.Menu>
        {options.map(option => (
          <Dropdown.Item
            key={option.value}
            data-elm-id={`${elmId}_${option.value}`}
            text={option.text}
            value={option.value}
            onClick={() => onSelect?.(option.value)}
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default EnhancedDropdown
